import { useState, useEffect } from 'react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import type { DateRange } from '../types';

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

type QuickRange = 'today' | '7days' | '30days' | 'all' | 'custom';

export function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [quickRange, setQuickRange] = useState<QuickRange>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Update custom inputs when value changes externally
  useEffect(() => {
    if (value.from) {
      setCustomFrom(format(value.from, "yyyy-MM-dd'T'HH:mm"));
    }
    if (value.to) {
      setCustomTo(format(value.to, "yyyy-MM-dd'T'HH:mm"));
    }
  }, [value]);

  const handleQuickRangeChange = (range: QuickRange) => {
    setQuickRange(range);
    
    const now = new Date();
    
    switch (range) {
      case 'today':
        onChange({
          from: startOfDay(now),
          to: endOfDay(now),
        });
        break;
      case '7days':
        onChange({
          from: startOfDay(subDays(now, 7)),
          to: endOfDay(now),
        });
        break;
      case '30days':
        onChange({
          from: startOfDay(subDays(now, 30)),
          to: endOfDay(now),
        });
        break;
      case 'all':
        onChange({ from: null, to: null });
        break;
      case 'custom':
        // Keep current values when switching to custom
        break;
    }
  };

  const handleCustomFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomFrom(e.target.value);
    setQuickRange('custom');
    
    const from = e.target.value ? new Date(e.target.value) : null;
    onChange({ ...value, from });
  };

  const handleCustomToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTo(e.target.value);
    setQuickRange('custom');
    
    const to = e.target.value ? new Date(e.target.value) : null;
    onChange({ ...value, to });
  };

  return (
    <div className="flex flex-row items-center gap-2 sm:gap-3 flex-nowrap sm:flex-wrap">
      {/* Quick range buttons */}
      <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200 shrink-0">
        {[
          { key: 'today', label: 'Today', short: 'Today' },
          { key: '7days', label: '7 Days', short: '7' },
          { key: '30days', label: '30 Days', short: '30' },
          { key: 'all', label: 'All Time', short: 'All' },
        ].map(({ key, label, short }) => (
          <button
            key={key}
            onClick={() => handleQuickRangeChange(key as QuickRange)}
            className={`px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
              quickRange === key
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
            }`}
          >
            <span className={short !== label ? "hidden sm:inline" : ""}>{label}</span>
            {short !== label && <span className="sm:hidden">{short}</span>}
          </button>
        ))}
      </div>

      {/* Custom range inputs */}
      <div className="flex items-center gap-2 shrink-0">
        <label className="text-xs text-slate-500 shrink-0 hidden sm:block">From:</label>
        <div className="relative flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto bg-slate-50 sm:bg-transparent rounded-lg border border-slate-200 sm:border-none">
          <svg className="w-4 h-4 sm:hidden text-slate-600 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <input
            type="datetime-local"
            value={customFrom}
            onChange={handleCustomFromChange}
            title="From"
            className="absolute inset-0 opacity-0 sm:opacity-100 sm:relative sm:input-pill w-full sm:w-48 cursor-pointer"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <label className="text-xs text-slate-500 shrink-0 hidden sm:block">To:</label>
        <div className="relative flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto bg-slate-50 sm:bg-transparent rounded-lg border border-slate-200 sm:border-none">
          <svg className="w-4 h-4 sm:hidden text-slate-600 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <input
            type="datetime-local"
            value={customTo}
            onChange={handleCustomToChange}
            title="To"
            className="absolute inset-0 opacity-0 sm:opacity-100 sm:relative sm:input-pill w-full sm:w-48 cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
}
